import {
  CalendarOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Col, List, Row, Space } from 'antd';
import Title from 'antd/lib/typography/Title';
import React, { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { buildFullName, buildTimestampDate } from '../../helpers/formatters';
import { formatAccountPhoneNumber, useCurrentUser } from '../../helpers/utils';
import styles from '../../pages/user/user.module.scss';
import { Core_Sex, Patientportal_PortalUserMsg } from '../../src/generated/graphql';
import { ChangeBirthDateModal } from '../change-birth-date-modal';
import { ChangeNameModal } from '../change-name-modal';
import { ChangePasswordModal } from '../change-password-modal';
import { SexIcon } from '../common-layout/sex-icon';

interface PortalUserListItem {
  id: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  onClick?: () => void;
}

interface PortalUserListGroup {
  key: string;
  title: string;
  items: PortalUserListItem[];
}

interface UserDetailsProps {
  userOverride?: Patientportal_PortalUserMsg;
  reduced?: boolean;
  hidePassword?: boolean;
}

export const UserDetails: FC<UserDetailsProps> = ({ userOverride, reduced, hidePassword }) => {
  const { formatMessage } = useIntl();

  const {
    currentUser,
    userLoading: currentUserLoading,
    refetch: refetchCurrentUser,
  } = useCurrentUser();
  const user = userOverride ?? currentUser;

  const [changePasswordOpen, setChangePasswordOpen] = useState<boolean>(false);
  const [changeNameOpen, setChangeNameOpen] = useState<boolean>(false);
  const [changeBirthDateOpen, setChangeBirthDateOpen] = useState<boolean>(false);

  const persona: PortalUserListItem[] = [
    {
      id: 'user.name',
      icon: <UserOutlined />,
      content: (
        <Space direction="vertical" size={0}>
          <Space size={5}>
            {user?.sex && user?.sex !== Core_Sex.SexNone && (
              <SexIcon sex={user?.sex} />
            )}
            {buildFullName(user?.name)}
          </Space>
          <Button
            type="link"
            style={{ margin: 0, padding: 0, textAlign: 'left' }}
            onClick={() => setChangeNameOpen(true)}
          >
            {formatMessage({ id: 'user.name.description' })}
          </Button>
        </Space>
      ),
    },
    {
      id: 'user.birthday',
      icon: <CalendarOutlined />,
      content: (
        <Space direction="vertical" size={0}>
          <span>{buildTimestampDate(user?.dateOfBirth)}</span>
          <Button
            type="link"
            style={{ margin: 0, padding: 0, textAlign: 'left' }}
            onClick={() => setChangeBirthDateOpen(true)}
          >
            {formatMessage({ id: 'user.birthday.description' })}
          </Button>
        </Space>
      ),
    },
    {
      id: 'user.password',
      icon: <LockOutlined />,
      content: (
        <Button
          type="link"
          style={{
            margin: 0,
            padding: 0,
            textAlign: 'left',
          }}
          onClick={() => setChangePasswordOpen(true)}
        >
          {formatMessage({ id: 'user.password.description' })}
        </Button>
      ),
    },
  ];

  const accountPhoneNumber = formatAccountPhoneNumber({
    phoneNumber: user?.phoneNumber,
    countryCode: user?.phoneNumberCountryCode,
  });

  const contact: PortalUserListItem[] = [
    {
      id: 'user.email',
      icon: <MailOutlined />,
      content: user?.email,
    },
    {
      id: 'user.phone',
      icon: <PhoneOutlined />,
      content: accountPhoneNumber ? accountPhoneNumber : '-',
    },
  ];

  const groups: PortalUserListGroup[] = [
    {
      key: 'user',
      title: formatMessage({ id: 'user.persona' }),
      items: persona,
    },
    {
      key: 'contact',
      title: formatMessage({ id: 'user.contact' }),
      items: contact,
    },
  ];

  const omitWhenReduced: string[] = ['user.password', 'contact'];

  return (
    <div>
      <Space style={{ width: '100%' }} direction={'vertical'} size={8}>
        {groups
          ?.filter((group) => !reduced || !omitWhenReduced.includes(group.key))
          ?.map((group) => (
            <div key={group.key} className={styles.group}>
              <Row>
                <Col span={24}>
                  <Title level={3} style={{ fontWeight: 'bold' }}>
                    {group.title}
                  </Title>
                </Col>
              </Row>
              <List
                dataSource={group.items?.filter(
                  (item) =>
                    (!reduced || !omitWhenReduced.includes(item.id)) &&
                    (!hidePassword || item.id !== 'user.password'),
                )}
                renderItem={(item) => (
                  <List.Item
                    key={item.id}
                    onClick={item.onClick}
                    className={item.onClick ? styles.listItemSelectable : undefined}
                  >
                    <List.Item.Meta
                      avatar={item.icon}
                      title={<b>{formatMessage({ id: item.id })}</b>}
                      description={item.content}
                    />
                  </List.Item>
                )}
              />
            </div>
          ))}
      </Space>
      {changePasswordOpen && (
        <ChangePasswordModal
          currentUser={user}
          currentUserLoading={currentUserLoading ?? false}
          refetchCurrentUser={refetchCurrentUser}
          open={changePasswordOpen}
          onCancel={() => setChangePasswordOpen(false)}
        />
      )}
      {changeNameOpen && (
        <ChangeNameModal
          currentUser={user}
          currentUserLoading={currentUserLoading ?? false}
          open={changeNameOpen}
          onClose={() => setChangeNameOpen(false)}
        />
      )}
      {changeBirthDateOpen && (
        <ChangeBirthDateModal
          currentUser={user}
          currentUserLoading={currentUserLoading ?? false}
          open={changeBirthDateOpen}
          onClose={() => setChangeBirthDateOpen(false)}
        />
      )}
    </div>
  );
};
